import { CdkAccordion, CdkAccordionItem } from '@angular/cdk/accordion';
import { CommonModule } from '@angular/common';
import {
  Component,
  computed,
  DestroyRef,
  effect,
  forwardRef,
  inject,
  input,
  OnInit,
  output,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  FormArray,
  FormControl,
  FormsModule,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatOption } from '@angular/material/autocomplete';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { MatSelect } from '@angular/material/select';
import { TranslateModule } from '@ngx-translate/core';
import { FunctionParameterParameterLocation } from '@vendasta/ai-assistants';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import { GalaxyTooltipModule } from '@vendasta/galaxy/tooltip';
import { distinctUntilChanged, tap } from 'rxjs';
import { AiToolParameterForm } from '../../../core/forms';

export enum ParameterType {
  STRING = 'string',
  NUMBER = 'number',
  BOOLEAN = 'boolean',
  INTEGER = 'integer',
  OBJECT = 'object',
  ARRAY = 'array',
  ENUM = 'enum',
}

@Component({
  selector: 'ai-tool-parameter-form',
  imports: [
    FormsModule,
    ReactiveFormsModule,
    GalaxyFormFieldModule,
    MatIcon,
    MatIconButton,
    MatInput,
    MatOption,
    MatSelect,
    MatCheckboxModule,
    CommonModule,
    CdkAccordion,
    CdkAccordionItem,
    MatButton,
    TranslateModule,
    GalaxyTooltipModule,
  ],
  templateUrl: './ai-tool-parameter-form.component.html',
  styleUrl: './ai-tool-parameter-form.component.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => AiToolParameterFormComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => AiToolParameterFormComponent),
      multi: true,
    },
  ],
})
export class AiToolParameterFormComponent implements OnInit {
  ParameterType = ParameterType;
  ParameterLocation = FunctionParameterParameterLocation;

  parameterLocationOptions = [
    { value: FunctionParameterParameterLocation.LOCATION_BODY, label: 'Body' },
    { value: FunctionParameterParameterLocation.LOCATION_QUERY_PARAM, label: 'Query' },
  ];

  private readonly destroyRef = inject(DestroyRef);

  readonly form = input.required<AiToolParameterForm>();
  readonly parentForm = input<AiToolParameterForm | undefined>();

  readonly isNestedField = computed<boolean>(() => this.parentForm() !== undefined);
  readonly isArrayItem = computed<boolean>(() => this.parentForm()?.controls?.type?.value === ParameterType.ARRAY);
  readonly remove = output();

  autofillValue = true;

  readonly parameterTypeOptions = computed(() => {
    const allParameterTypes = [
      { value: ParameterType.STRING, label: 'AI_ASSISTANT.FUNCTIONS.PARAMETER.TYPE.STRING' },
      { value: ParameterType.NUMBER, label: 'AI_ASSISTANT.FUNCTIONS.PARAMETER.TYPE.NUMBER' },
      { value: ParameterType.BOOLEAN, label: 'AI_ASSISTANT.FUNCTIONS.PARAMETER.TYPE.BOOLEAN' },
      { value: ParameterType.INTEGER, label: 'AI_ASSISTANT.FUNCTIONS.PARAMETER.TYPE.INTEGER' },
      { value: ParameterType.OBJECT, label: 'AI_ASSISTANT.FUNCTIONS.PARAMETER.TYPE.OBJECT' },
      { value: ParameterType.ARRAY, label: 'AI_ASSISTANT.FUNCTIONS.PARAMETER.TYPE.ARRAY' },
      { value: ParameterType.ENUM, label: 'AI_ASSISTANT.FUNCTIONS.PARAMETER.TYPE.ENUM' },
    ];

    // We do not allow the Object type in query params
    const removeObjectType = this.locationValue() === FunctionParameterParameterLocation.LOCATION_QUERY_PARAM;
    // We do not allow array types in query params
    const removeArrayType = this.locationValue() === FunctionParameterParameterLocation.LOCATION_QUERY_PARAM;

    return allParameterTypes.filter((type) =>
      type.value === ParameterType.OBJECT
        ? !removeObjectType
        : type.value === ParameterType.ARRAY
          ? !removeArrayType
          : true,
    );
  });

  // We need to react intelligently to a number of form controls - so we map their `valueChanges` observables to signals
  private locationValue = signal<FunctionParameterParameterLocation | null>(null);
  private parentLocationValue = signal<FunctionParameterParameterLocation | null | undefined>(undefined);
  private typeValue = signal<string | null>(null);

  constructor() {
    // Location should not be editable when type is object or array
    effect(() => {
      if (this.typeValue() === ParameterType.OBJECT || this.typeValue() === ParameterType.ARRAY) {
        this.form().controls.location.disable();
      } else if (this.form().enabled) {
        this.form().controls.location.enable();
      }

      if (this.typeValue() === ParameterType.ARRAY && this.form().controls.items === undefined) {
        this.form().setControl('items', new AiToolParameterForm({}, true));
      }
    });
  }

  ngOnInit() {
    this.locationValue.set(this.form().controls.location.value);
    this.form()
      .controls.location.valueChanges.pipe(
        distinctUntilChanged(),
        tap((value) => this.locationValue.set(value)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();

    this.autofillValue = !this.form()?.controls?.value?.value;

    this.parentLocationValue.set(this.parentForm()?.controls.location.value);
    this.parentForm()
      ?.controls.location.valueChanges.pipe(
        distinctUntilChanged(),
        tap((value) => this.parentLocationValue.set(value)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();

    this.typeValue.set(this.form().controls.type.value);
    this.form()
      .controls.type.valueChanges.pipe(
        distinctUntilChanged(),
        tap((value) => this.typeValue.set(value)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  handleParamAdd() {
    this.form()?.controls.nestedParameters?.push(this.generateChildParameterForm());
  }

  handleParamRemove(index: number) {
    this.form()?.controls.nestedParameters?.removeAt(index);
  }

  generateChildParameterForm() {
    const form = new AiToolParameterForm({ location: this.form().controls.location.value ?? undefined });
    if (this.form().disabled) {
      form.disable();
    }

    return form;
  }

  handleEnumAdd(value?: string) {
    this.form().controls.enumValues?.push(new FormControl(value || '', Validators.required));
  }

  handleEnumRemove(index: number) {
    this.form().controls.enumValues?.removeAt(index);
  }

  autofillChanged(checked: boolean) {
    if (checked) {
      this.form()?.controls.value?.setValue(null);
    }
  }

  parameterTypeChanged(newType: ParameterType) {
    if (newType === ParameterType.ENUM) {
      this.form().controls.enumValues = new FormArray<FormControl<string | null>>([
        new FormControl('', Validators.required),
      ]);
    } else {
      this.form().controls.enumValues?.clear();
    }
  }
}
